/**
 * View Info — GIS identify panel for a picked vector feature.
 *
 * Two-column Feature / Value table with Derived (clicked coords, area,
 * length) and Attributes groups. Same layout for polygons and points.
 */
import { useState } from 'react';
import { Button, Icon, Stack, Text, tokens } from '@/design';
import type { IdentifySection, IdentifySnapshot } from './vectorIdentifyModel';

export interface VectorViewInfoPanelProps {
  readonly armed: boolean;
  readonly snapshot: IdentifySnapshot | null;
  readonly onArm: () => void;
  readonly onClear: () => void;
  readonly 'data-testid'?: string;
}

export function VectorViewInfoPanel({
  armed,
  snapshot,
  onArm,
  onClear,
  'data-testid': testId = 'vector-view-info',
}: VectorViewInfoPanelProps) {
  return (
    <Stack direction="column" gap={2} data-testid={testId}>
      <Stack direction="row" align="center" gap={2}>
        <Icon name="info" size={20} decorative />
        <Text variant="subtitle" as="h3" style={{ margin: 0 }}>
          View Info
        </Text>
      </Stack>
      <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
        <Button
          variant={armed ? 'secondary' : 'ghost'}
          onClick={onArm}
          aria-pressed={armed}
          data-testid={`${testId}-arm`}
        >
          <Icon name="my_location" size={20} decorative />
          <span>{armed ? 'Click a feature on the map' : 'View Info'}</span>
        </Button>
        {snapshot ? (
          <Button variant="ghost" onClick={onClear} data-testid={`${testId}-clear`}>
            <Icon name="close" size={20} decorative />
            <span>Clear</span>
          </Button>
        ) : null}
      </Stack>
      {armed && !snapshot ? (
        <Text variant="caption" as="p" style={{ margin: 0, color: tokens.color.ink.secondary }}>
          Click a district, area, or point on the map to inspect it.
        </Text>
      ) : null}
      {snapshot ? <IdentifyTable snapshot={snapshot} testId={testId} /> : null}
    </Stack>
  );
}

function IdentifyTable({
  snapshot,
  testId,
}: {
  readonly snapshot: IdentifySnapshot;
  readonly testId: string;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({
    derived: true,
    attributes: true,
  });

  return (
    <div
      data-testid={`${testId}-table`}
      style={{
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.md,
        overflow: 'hidden',
        background: tokens.color.surface[1],
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.sm,
        }}
      >
        <thead>
          <tr style={{ background: tokens.color.surface[2] }}>
            <th scope="col" style={headerStyle()}>Feature</th>
            <th scope="col" style={headerStyle()}>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ background: tokens.color.semantic.warning }}>
            <td colSpan={2} style={cellStyle(true)}>
              <strong>{snapshot.title}</strong>
            </td>
          </tr>
          {snapshot.sections.map((section) => (
            <IdentifySectionRows
              key={section.id}
              section={section}
              open={open[section.id] !== false}
              onToggle={() =>
                setOpen((prev) => ({ ...prev, [section.id]: prev[section.id] === false }))
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IdentifySectionRows({
  section,
  open,
  onToggle,
}: {
  readonly section: IdentifySection;
  readonly open: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <>
      <tr>
        <td colSpan={2} style={{ padding: 0 }}>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            style={{
              appearance: 'none',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
              padding: `${tokens.space[2]}px ${tokens.space[2]}px`,
              background: tokens.color.surface[2],
              border: 0,
              color: tokens.color.ink.secondary,
              cursor: 'pointer',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.xs,
              fontWeight: tokens.font.weight.semibold,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              textAlign: 'left',
            }}
          >
            <Icon name={open ? 'expand_more' : 'chevron_right'} size={16} decorative />
            {section.title}
          </button>
        </td>
      </tr>
      {open
        ? section.rows.map((row, index) => (
            <tr
              key={`${section.id}-${row.feature}`}
              style={{
                background: index % 2 === 1 ? tokens.color.surface[2] : tokens.color.surface[1],
              }}
            >
              <td style={cellStyle(false)}>{row.feature}</td>
              <td style={cellStyle(true)}>{row.value}</td>
            </tr>
          ))
        : null}
    </>
  );
}

function headerStyle() {
  return {
    textAlign: 'left' as const,
    padding: `${tokens.space[2]}px`,
    fontSize: tokens.font.size.xs,
    fontWeight: tokens.font.weight.semibold,
    color: tokens.color.ink.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
  };
}

function cellStyle(mono: boolean) {
  return {
    padding: `${tokens.space[2]}px`,
    borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[2]}`,
    fontFamily: mono ? tokens.font.family.mono : tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
    color: tokens.color.ink.primary,
    wordBreak: 'break-word' as const,
  };
}
